import resolveConfig from 'tailwindcss/lib/public/resolve-config';

import customTailwindConfig from '../../../../tailwind.config';

import { getImageClass, getCloudinaryImage } from '../Media.constants';

const tailwindConfig = resolveConfig(customTailwindConfig);
const SCREENS = tailwindConfig.theme.screens;
const SIZE_OPTIONS = {
  // Full width images
  full: '100vw',
  // Half width images, full width on mobile
  half: `
    (max-width: ${SCREENS.md}) 100,
    50vw
  `,
  // Full width images within containers, so these images will have the width of a container
  container: `
    (max-width: ${SCREENS['2xl']}) calc(100vw),
    ${SCREENS['2xl']}
  `,
  // Images with half a container's width, full width on mobile
  'half-container': `
    (max-width: ${SCREENS.md}) calc(100vw),
    (max-width: ${SCREENS['2xl']}) calc(50vw),
    calc(${SCREENS['2xl']} / 2)
  `,
  // Images on grid cards
  'grid-card': `
    (max-width: ${SCREENS.md}) calc(100vw),
    (max-width: ${SCREENS['2xl']}) calc((100vw) / 3),
    calc((${SCREENS['2xl']}) / 3)
  `,
  chef: `300px`,
};
const BREAKPOINTS = [640, 1024, 1536, 2560];

const getCloudinarySrc = (url) => {
  let defaultOptimisation = `c_fill,q_auto,f_auto,g_auto`;
  const transformations = url.raw_transformation
    .split('/')
    .map((t) => t.split(',')) // Convert transformations into a single array
    .reduce((a, c) => [...a, ...c], [])
    .filter((t) => !t.startsWith('f_') && !t.startsWith('q_')); // Remove format and quality optimisations because these are device dependent

  if (transformations.length > 0) {
    defaultOptimisation = ['f_auto', 'q_auto'];

    // Add c_fill?
    if (!transformations.find((t) => t.startsWith('c_'))) {
      defaultOptimisation.push('c_fill');
    }

    // Add gravity?
    if (!transformations.find((t) => t.startsWith('g_'))) {
      defaultOptimisation.push('g_auto');
    }

    defaultOptimisation = [...defaultOptimisation, ...transformations].join(
      ','
    );
  }

  const srcSet = BREAKPOINTS.map(
    (w) =>
      getCloudinaryImage(url, 'jpg', `w_${w},ar_16:10,${defaultOptimisation}`) +
      ` ${w}w`
  ).join(',\n');
  const verticalSrcSet = BREAKPOINTS.map(
    (w) =>
      getCloudinaryImage(url, 'jpg', `w_${w},ar_9:16,${defaultOptimisation}`) +
      ` ${w}w`
  ).join(',\n');
  const gridSrcSet = BREAKPOINTS.map(
    (w) =>
      getCloudinaryImage(url, 'jpg', `w_${w},ar_1:1,${defaultOptimisation}`) +
      ` ${w}w`
  ).join(',\n');
  const portraitSrcSet = BREAKPOINTS.map(
    (w) =>
      getCloudinaryImage(url, 'jpg', `w_${w},ar_4:5,${defaultOptimisation}`) +
      ` ${w}w`
  ).join(',\n');
  const fallbackSrc = getCloudinaryImage(
    url,
    'jpg',
    `w_1280,${defaultOptimisation}`
  );
  return {
    srcSet,
    gridSrcSet,
    verticalSrcSet,
    portraitSrcSet,
    fallbackSrc,
  };
};
const getNonCloudinarySrc = (url) => {
  const newUrl = url.replace('downloads', 'images'); // contentful sometimes sends downloads URL?\
  const srcSet = BREAKPOINTS.map(
    (w) => `${newUrl}?fm=webp&q=80&w=${w}&h=${w * (10 / 16)}&fit=fill ${w}w`
  ).join(',\n');
  const gridSrcSet = BREAKPOINTS.map(
    (w) => `${newUrl}?fm=webp&q=80&w=${w}&h=${w}&fit=fill ${w}w`
  ).join(',\n');
  const verticalSrcSet = BREAKPOINTS.map(
    (w) =>
      `${newUrl}?fm=webp&q=80&w=${w}&h=${Math.round(
        w * (16 / 9)
      )}&fit=fill ${w}w`
  ).join(',\n');
  const portraitSrcSet = BREAKPOINTS.map(
    (w) =>
      `${newUrl}?fm=webp&q=80&w=${w}&h=${Math.round(
        w * (4 / 5)
      )}&fit=fill ${w}w`
  ).join(',\n');
  const fallbackSrc = `${newUrl}?fm=jpg&q=80&w=1280&h=720&fit=fill`;
  return {
    srcSet,
    gridSrcSet,
    verticalSrcSet,
    portraitSrcSet,
    fallbackSrc,
  };
};

function Image({ url, title = '', style, container = 'full', cloudinary = false }) {
  const imageClass = getImageClass({ style });
  let { srcSet, gridSrcSet, verticalSrcSet, fallbackSrc, portraitSrcSet } = cloudinary
    ? getCloudinarySrc(url)
    : getNonCloudinarySrc(url);
  return (
    <picture className={imageClass}>
      <source
        media="(max-width: 768px)"
        srcSet={
          container === 'full'
            ? verticalSrcSet
            : container === 'grid-card'
            ? gridSrcSet
            : container === 'portrait'
            ? portraitSrcSet
            : srcSet
        }
        sizes={SIZE_OPTIONS[container]}
      />
      <source
        media="(min-width: 768px)"
        srcSet={container === 'grid-card' ? gridSrcSet : container === 'portrait' ? portraitSrcSet : srcSet}
        sizes={SIZE_OPTIONS[container]}
      />
      <img src={fallbackSrc} alt={title || ''} className={imageClass} />
    </picture>
  );
}

export default Image;
